import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  signIn,
  signUp,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  signInWithRedirect,
} from 'aws-amplify/auth';
import VisionLogo from '../../assets/VisionLogo.png';

// Views: 'signin' | 'signup' | 'confirm_signup' | 'forgot_password' | 'confirm_reset'
const VIEWS = {
  SIGNIN: 'signin',
  SIGNUP: 'signup',
  CONFIRM_SIGNUP: 'confirm_signup',
  FORGOT_PASSWORD: 'forgot_password',
  CONFIRM_RESET: 'confirm_reset',
};

export default function LoginPage() {
  const navigate = useNavigate();

  const [view, setView] = useState(VIEWS.SIGNIN);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmationCode, setConfirmationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const clearMessages = () => {
    setError('');
    setSuccessMessage('');
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await signIn({ username: email, password });
      navigate('/home');
    } catch (err) {
      setError(err.message || 'Sign in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    clearMessages();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      await signUp({
        username: email,
        password,
        options: { userAttributes: { email } },
      });
      setSuccessMessage('Account created! Check your email for a confirmation code.');
      setView(VIEWS.CONFIRM_SIGNUP);
    } catch (err) {
      setError(err.message || 'Sign up failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await confirmSignUp({ username: email, confirmationCode });
      setSuccessMessage('Email confirmed! You can now sign in.');
      setView(VIEWS.SIGNIN);
      setConfirmationCode('');
    } catch (err) {
      setError(err.message || 'Confirmation failed. Please check your code.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    clearMessages();
    setLoading(true);
    try {
      await resetPassword({ username: email });
      setSuccessMessage('Reset code sent! Check your email.');
      setView(VIEWS.CONFIRM_RESET);
    } catch (err) {
      setError(err.message || 'Could not send reset code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    clearMessages();
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode,
        newPassword,
      });
      setSuccessMessage('Password reset successful! You can now sign in.');
      setView(VIEWS.SIGNIN);
      setConfirmationCode('');
      setNewPassword('');
    } catch (err) {
      setError(err.message || 'Reset failed. Please check your code and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    signInWithRedirect({ provider: 'Google' });
  };

  const switchToView = (newView) => {
    clearMessages();
    setView(newView);
  };

  const renderTitle = () => {
    switch (view) {
      case VIEWS.SIGNUP: return 'Create Account';
      case VIEWS.CONFIRM_SIGNUP: return 'Confirm Your Email';
      case VIEWS.FORGOT_PASSWORD: return 'Reset Password';
      case VIEWS.CONFIRM_RESET: return 'Enter New Password';
      default: return 'Welcome Back';
    }
  };

  const renderSubtitle = () => {
    switch (view) {
      case VIEWS.SIGNUP: return 'Join SixtyFour and sharpen your chess vision.';
      case VIEWS.CONFIRM_SIGNUP: return `We sent a code to ${email}`;
      case VIEWS.FORGOT_PASSWORD: return 'Enter your email to receive a reset code.';
      case VIEWS.CONFIRM_RESET: return `Enter the code sent to ${email}`;
      default: return 'Sign in to continue your training.';
    }
  };

  return (
    <div className="min-h-screen bg-base-100 flex flex-col items-center justify-center px-4">
      {/* Logo */}
      <img
        src={VisionLogo}
        alt="SixtyFour Logo"
        className="w-20 h-20 mb-6 cursor-pointer"
        onClick={() => navigate('/')}
      />

      {/* Card */}
      <div className="bg-base-200 rounded-2xl shadow-2xl w-full max-w-md p-8">
        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold text-white">{renderTitle()}</h1>
          <p className="text-sm text-gray-400 mt-1">{renderSubtitle()}</p>
        </div>

        {/* Error / Success messages */}
        {error && (
          <div className="alert alert-error mb-4 text-sm rounded-xl py-2 px-4">
            <span>{error}</span>
          </div>
        )}
        {successMessage && (
          <div className="alert alert-success mb-4 text-sm rounded-xl py-2 px-4">
            <span>{successMessage}</span>
          </div>
        )}

        {/* --- SIGN IN --- */}
        {view === VIEWS.SIGNIN && (
          <form onSubmit={handleSignIn} className="flex flex-col gap-4">
            <div className="form-control">
              <label className="label pb-1">
                <span className="label-text text-gray-300">Email</span>
              </label>
              <input
                type="email"
                className="input input-bordered w-full bg-base-300"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="form-control">
              <label className="label pb-1">
                <span className="label-text text-gray-300">Password</span>
                <button
                  type="button"
                  className="label-text-alt text-primary hover:underline"
                  onClick={() => switchToView(VIEWS.FORGOT_PASSWORD)}
                >
                  Forgot password?
                </button>
              </label>
              <input
                type="password"
                className="input input-bordered w-full bg-base-300"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
              />
            </div>
            <button
              type="submit"
              className={`btn btn-primary w-full mt-2 ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        )}

        {/* --- SIGN UP --- */}
        {view === VIEWS.SIGNUP && (
          <form onSubmit={handleSignUp} className="flex flex-col gap-4">
            <div className="form-control">
              <label className="label pb-1">
                <span className="label-text text-gray-300">Email</span>
              </label>
              <input
                type="email"
                className="input input-bordered w-full bg-base-300"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <div className="form-control">
              <label className="label pb-1">
                <span className="label-text text-gray-300">Password</span>
              </label>
              <input
                type="password"
                className="input input-bordered w-full bg-base-300"
                placeholder="Min. 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <div className="form-control">
              <label className="label pb-1">
                <span className="label-text text-gray-300">Confirm Password</span>
              </label>
              <input
                type="password"
                className="input input-bordered w-full bg-base-300"
                placeholder="Re-enter password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <button
              type="submit"
              className={`btn btn-primary w-full mt-2 ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? 'Creating account…' : 'Create Account'}
            </button>
          </form>
        )}

        {/* --- CONFIRM SIGN UP --- */}
        {view === VIEWS.CONFIRM_SIGNUP && (
          <form onSubmit={handleConfirmSignUp} className="flex flex-col gap-4">
            <div className="form-control">
              <label className="label pb-1">
                <span className="label-text text-gray-300">Confirmation Code</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full bg-base-300 tracking-widest text-center text-lg"
                placeholder="123456"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                required
                maxLength={6}
              />
            </div>
            <button
              type="submit"
              className={`btn btn-primary w-full mt-2 ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? 'Confirming…' : 'Confirm Email'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm w-full"
              onClick={() => switchToView(VIEWS.SIGNIN)}
            >
              Back to Sign In
            </button>
          </form>
        )}

        {/* --- FORGOT PASSWORD --- */}
        {view === VIEWS.FORGOT_PASSWORD && (
          <form onSubmit={handleForgotPassword} className="flex flex-col gap-4">
            <div className="form-control">
              <label className="label pb-1">
                <span className="label-text text-gray-300">Email</span>
              </label>
              <input
                type="email"
                className="input input-bordered w-full bg-base-300"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
              />
            </div>
            <button
              type="submit"
              className={`btn btn-primary w-full mt-2 ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? 'Sending…' : 'Send Reset Code'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm w-full"
              onClick={() => switchToView(VIEWS.SIGNIN)}
            >
              Back to Sign In
            </button>
          </form>
        )}

        {/* --- CONFIRM RESET --- */}
        {view === VIEWS.CONFIRM_RESET && (
          <form onSubmit={handleConfirmReset} className="flex flex-col gap-4">
            <div className="form-control">
              <label className="label pb-1">
                <span className="label-text text-gray-300">Reset Code</span>
              </label>
              <input
                type="text"
                className="input input-bordered w-full bg-base-300 tracking-widest text-center text-lg"
                placeholder="123456"
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                required
                maxLength={6}
              />
            </div>
            <div className="form-control">
              <label className="label pb-1">
                <span className="label-text text-gray-300">New Password</span>
              </label>
              <input
                type="password"
                className="input input-bordered w-full bg-base-300"
                placeholder="Min. 8 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </div>
            <button
              type="submit"
              className={`btn btn-primary w-full mt-2 ${loading ? 'loading' : ''}`}
              disabled={loading}
            >
              {loading ? 'Resetting…' : 'Reset Password'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm w-full"
              onClick={() => switchToView(VIEWS.SIGNIN)}
            >
              Back to Sign In
            </button>
          </form>
        )}

        {/* Divider + Google + toggle only on sign in / sign up */}
        {(view === VIEWS.SIGNIN || view === VIEWS.SIGNUP) && (
          <>
            <div className="divider text-gray-500 text-xs my-5">OR</div>

            {/* Google SSO */}
            <button
              type="button"
              onClick={handleGoogleSignIn}
              className="btn btn-outline w-full mb-4 flex items-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </button>

            {/* Toggle between sign in / sign up */}
            <p className="text-center text-sm text-gray-400">
              {view === VIEWS.SIGNIN ? (
                <>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    className="text-primary hover:underline font-medium"
                    onClick={() => switchToView(VIEWS.SIGNUP)}
                  >
                    Sign Up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    className="text-primary hover:underline font-medium"
                    onClick={() => switchToView(VIEWS.SIGNIN)}
                  >
                    Sign In
                  </button>
                </>
              )}
            </p>
          </>
        )}
      </div>

      {/* Guest / Demo link */}
      <p className="mt-6 text-sm text-gray-500">
        Just browsing?{' '}
        <button
          type="button"
          className="text-primary hover:underline font-medium"
          onClick={() => navigate('/demo')}
        >
          Continue as Guest / Try Demo
        </button>
      </p>
    </div>
  );
}
